import React, { createContext, useReducer, useEffect, useContext } from "react";
import type {
  User,
  LoginCredentials,
  RegisterData,
  AuthState,
} from "../types/auth";
import authService from "../services/authService";

// === INTERFACE DO CONTEXTO ===
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

// === ACTIONS DO REDUCER ===
type AuthAction =
  | { type: "AUTH_START" }
  | { type: "AUTH_SUCCESS"; payload: User }
  | { type: "AUTH_LOGOUT" }
  | { type: "AUTH_ERROR"; payload: string }
  | { type: "CLEAR_ERROR" };

// === REDUCER ===
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "AUTH_START":
      return {
        ...state,
        loading: true,
        error: null,
      };

    case "AUTH_SUCCESS":
      return {
        user: action.payload,
        isAuthenticated: true,
        loading: false,
        error: null,
      };

    case "AUTH_LOGOUT":
      return {
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };

    case "AUTH_ERROR":
      return {
        user: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload,
      };

    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
}

// === ESTADO INICIAL ===
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

// === CONTEXTO ===
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

// === PROVIDER ===
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // === MÉTODOS DE AUTENTICAÇÃO ===

  /**
   * Realiza login usando AuthService
   */
  const login = async (credentials: LoginCredentials) => {
    dispatch({ type: "AUTH_START" });

    try {
      const user = await authService.login(credentials);
      dispatch({ type: "AUTH_SUCCESS", payload: user });

      // Redirecionamento será feito automaticamente pelo App.tsx
      // baseado no estado isAuthenticated
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao fazer login";
      dispatch({ type: "AUTH_ERROR", payload: message });
      throw error; // Re-throw para componente capturar
    }
  };

  /**
   * Realiza logout usando AuthService
   */
  const logout = async () => {
    dispatch({ type: "AUTH_START" });

    try {
      await authService.logout();
      dispatch({ type: "AUTH_LOGOUT" });
      window.location.href = "/login";
    } catch {
      dispatch({ type: "AUTH_LOGOUT" });
      window.location.href = "/login";
    }
  };

  /**
   * Registra usuário usando AuthService
   */
  const register = async (userData: RegisterData) => {
    dispatch({ type: "AUTH_START" });

    try {
      await authService.register(userData);
      dispatch({ type: "CLEAR_ERROR" });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao registrar usuário";
      dispatch({ type: "AUTH_ERROR", payload: message });
      throw error;
    }
  };

  /**
   * Verifica se usuário está autenticado via sessão
   */
  const checkAuth = async () => {
    try {
      const user = await authService.getCurrentUser();
      dispatch({ type: "AUTH_SUCCESS", payload: user });
    } catch {
      dispatch({ type: "AUTH_LOGOUT" });
    }
  };

  /**
   * Limpa erros do estado
   */
  const clearError = () => {
    dispatch({ type: "CLEAR_ERROR" });
  };

  useEffect(() => {
    let montado = true;

    // Nao despacha se o provider ja saiu da arvore: o dispatch tardio
    // atualiza componente desmontado.
    (async () => {
      try {
        const user = await authService.getCurrentUser();
        if (montado) dispatch({ type: "AUTH_SUCCESS", payload: user });
      } catch {
        if (montado) dispatch({ type: "AUTH_LOGOUT" });
      }
    })();

    return () => {
      montado = false;
    };
  }, []);

  const value: AuthContextType = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    error: state.error,
    login,
    logout,
    register,
    checkAuth,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
}
